/**
 * Published sites (plan 7.1, 7.10): React Query hooks over `lib/sites-api`,
 * the `site:update` socket nudge and the availability check of the address
 * field. Every site query shares the `['sites']` prefix, so one invalidation
 * refreshes the list, the settings page and the Publish sheet together.
 */
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Socket } from 'socket.io-client';
import {
  SOCKET_EVENTS,
  type AgentArtifactSite,
  type AgentArtifactSummary,
  type AgentPublishingCapability,
  type PublishedSiteDetail,
  type SiteListTab,
  type SiteSocketEvent,
} from '@tabbio/types';
import { ensureValidAccessToken } from '../lib/app-auth';
import { QUERY_KEYS } from '../lib/query-keys';
import { rememberSiteBindings } from '../lib/site-compose';
import {
  checkSiteName,
  getArtifactHeadCheckErrors,
  getArtifactSite,
  getSite,
  getSiteDeploys,
  getSiteStats,
  listSites,
} from '../lib/sites-api';
import { getSocket, subscribeSocketLifecycle } from '../lib/socket';
import {
  SiteNameChecker,
  type SiteNameCheckState,
} from '../components/sites/site-name-check';
import { summarySiteFor } from '../components/sites/publish-sheet-model';
import { useAgentCapabilities } from './useAgentCapabilities';

/** Publishing for this account, or null while unknown (read as unavailable). */
export function usePublishingCapability(): AgentPublishingCapability | null {
  const { capabilities } = useAgentCapabilities();
  return capabilities?.publishing ?? null;
}

// When a site last changed from this app (publish, update, rename, unpublish,
// a settings save, or a `site:update` nudge): a summary's `site` read before
// then is out of date, so cards ask `GET /api/artifacts/:id/site` again.
let siteChangedAt = 0;
const siteChangeListeners = new Set<() => void>();

export function noteSiteChange(now = Date.now()) {
  siteChangedAt = Math.max(siteChangedAt, now);
  for (const listener of siteChangeListeners) listener();
}

function subscribeSiteChanges(listener: () => void) {
  siteChangeListeners.add(listener);
  return () => {
    siteChangeListeners.delete(listener);
  };
}

function useSiteChangedAt() {
  return useSyncExternalStore(subscribeSiteChanges, () => siteChangedAt, () => siteChangedAt);
}

export function resetSiteChangesForTests() {
  siteChangedAt = 0;
  siteChangeListeners.clear();
}

/** Everything about sites refreshes: the list, details, the artifact's site, capabilities. */
export function useInvalidateSites() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    noteSiteChange();
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.SITES });
    // The live-site count in the capabilities answer changes with a publish.
    void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.AGENT_CAPABILITIES });
  }, [queryClient]);
}

/**
 * The owner is nudged with `SOCKET_EVENTS.SITE` (`site:update`) whenever a
 * site or its deploy changes. Listen while `enabled`, following the socket
 * singleton across auth-driven recreation like the artifact nudge does.
 */
export function useSiteSocketNudge(
  enabled: boolean,
  onNudge: (event: SiteSocketEvent) => void,
) {
  const onNudgeRef = useRef(onNudge);
  onNudgeRef.current = onNudge;
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    setSocket(getSocket());
    return subscribeSocketLifecycle(setSocket);
  }, [enabled]);

  useEffect(() => {
    if (!enabled || !socket) return;
    let active = true;
    const handler = (event: SiteSocketEvent) => {
      if (event && typeof event.siteId === 'string') {
        noteSiteChange();
        onNudgeRef.current(event);
      }
    };
    socket.on(SOCKET_EVENTS.SITE, handler);
    if (!socket.connected) {
      void ensureValidAccessToken().then((token) => {
        if (active && token && !socket.connected) socket.connect();
      });
    }
    return () => {
      active = false;
      socket.off(SOCKET_EVENTS.SITE, handler);
    };
  }, [enabled, socket]);
}

/** `GET /api/sites` for a tab and a search, refreshed by the socket nudge. */
export function useSites(tab: SiteListTab, q: string, options: { enabled?: boolean } = {}) {
  const enabled = options.enabled ?? true;
  const query = useQuery({
    queryKey: QUERY_KEYS.SITES_LIST(tab, q.trim()),
    queryFn: () => listSites({ tab, q: q.trim() || undefined, limit: 50 }),
    enabled,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
  useEffect(() => {
    if (query.data) rememberSiteBindings(query.data.sites);
  }, [query.data]);
  const invalidate = useInvalidateSites();
  useSiteSocketNudge(enabled, invalidate);
  return query;
}

/** `GET /api/sites/:siteId` */
export function useSite(siteId: string | null | undefined) {
  const query = useQuery({
    queryKey: QUERY_KEYS.SITE(siteId ?? ''),
    queryFn: () => getSite(siteId as string),
    enabled: Boolean(siteId),
    staleTime: 15_000,
    retry: (count, error) => (error as { code?: string })?.code !== 'SITE_NOT_FOUND' && count < 2,
  });
  useEffect(() => {
    if (query.data) rememberSiteBindings([query.data]);
  }, [query.data]);
  return query;
}

/**
 * `GET /api/artifacts/:id/site`: the site of one artifact (null when it was
 * never published). Cards and the panel header read it for "Published at"
 * and the Live chip, so it is cached for a few minutes and refreshed by
 * every sites invalidation.
 */
export function useArtifactSite(
  artifactId: string | null | undefined,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: QUERY_KEYS.ARTIFACT_SITE(artifactId ?? ''),
    queryFn: () => getArtifactSite(artifactId as string),
    enabled: Boolean(artifactId) && (options.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

/**
 * A card's site ("Published at <host>"): the one the artifact's summary
 * carries (the summary the card already holds, with the time it was read),
 * so cards stop asking one by one; `GET /api/artifacts/:id/site` only as the
 * fallback when the summary has no `site`. Undefined while unknown.
 */
export function useArtifactCardSite(
  artifactId: string | null | undefined,
  summary: Pick<AgentArtifactSummary, 'id' | 'site'> | null | undefined,
  options: { enabled?: boolean; summaryAt?: number } = {},
): AgentArtifactSite | PublishedSiteDetail | null | undefined {
  const changedAt = useSiteChangedAt();
  const fromSummary = summarySiteFor(artifactId, summary, options.summaryAt ?? 0, changedAt);
  const enabled = options.enabled ?? true;
  const fallback = useArtifactSite(artifactId, { enabled: enabled && fromSummary === undefined });
  if (!enabled) return undefined;
  return fromSummary !== undefined ? fromSummary : fallback.data;
}

/** Blocking findings on the head version, known before a publish is tried. */
export function useArtifactHeadChecks(artifactId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.ARTIFACT_HEAD_CHECKS(artifactId ?? ''),
    queryFn: () => getArtifactHeadCheckErrors(artifactId as string),
    enabled: Boolean(artifactId) && enabled,
    staleTime: 30_000,
    retry: false,
  });
}

/** `GET /api/sites/:siteId/stats?days=` */
export function useSiteStats(siteId: string | null | undefined, days = 30, enabled = true) {
  return useQuery({
    queryKey: QUERY_KEYS.SITE_STATS(siteId ?? '', days),
    queryFn: () => getSiteStats(siteId as string, days),
    enabled: Boolean(siteId) && enabled,
    staleTime: 60_000,
  });
}

/** `GET /api/sites/:siteId/deploys` (Site history). */
export function useSiteDeploys(siteId: string | null | undefined, enabled: boolean) {
  return useQuery({
    queryKey: QUERY_KEYS.SITE_DEPLOYS(siteId ?? ''),
    queryFn: () => getSiteDeploys(siteId as string),
    enabled: Boolean(siteId) && enabled,
    staleTime: 15_000,
  });
}

/**
 * The address field's availability (debounced 300 ms, stale requests
 * cancelled) for the name as typed. `currentName` is the site's own address
 * when renaming; `enabled` false keeps it idle (the sheet is on another view).
 */
export function useSiteNameCheck(options: {
  name: string;
  enabled: boolean;
  artifactId?: string;
  currentName?: string | null;
  errorMessage?: string;
}) {
  const [state, setState] = useState<SiteNameCheckState>({ status: 'idle', name: '' });
  const [checker, setChecker] = useState<SiteNameChecker | null>(null);
  const { name, enabled, artifactId, currentName, errorMessage } = options;

  useEffect(() => {
    if (!enabled) {
      setState({ status: 'idle', name: '' });
      return;
    }
    const next = new SiteNameChecker(
      (value, signal) => checkSiteName(value, { artifactId, signal }),
      setState,
      { currentName: currentName ?? null, errorMessage },
    );
    setChecker(next);
    return () => {
      next.dispose();
      setChecker((current) => (current === next ? null : current));
    };
  }, [artifactId, currentName, enabled, errorMessage]);

  useEffect(() => {
    checker?.update(name);
  }, [checker, name]);

  return state;
}
